import { ValidationResult, useSprintMutation, useSprintQueryClient, SprintMutationResult } from "sprint-common-web";

import { UpdateOnPremVideoAssetTechnicalRequest } from "../../../../../../common/api/master-api/assets/details/UpdateOnPremVideoAssetTechnicalRequest";
import { UpdateOnPremVideoAssetTechnicalResponse } from "../../../../../../common/api/master-api/assets/details/UpdateOnPremVideoAssetTechnicalResponse";
import { useAssetsApi } from "../../../../../../common/api/master-api/assets/useAssetsApi";
import { OnPremAssetQueryKey } from "../../../common/OnPremAssetQueryKey";

export interface ValidateOnPremVideoAssetTechnicalParams {
    assetId?: string;
    aspectRatioId: string;
}

export interface UseEditOnPremAsset {
    validateOnPremVideoAssetTechnical: SprintMutationResult<ValidateOnPremVideoAssetTechnicalParams, ValidationResult>;
    updateOnPremVideoAssetTechnical: SprintMutationResult<
        UpdateOnPremVideoAssetTechnicalRequest,
        UpdateOnPremVideoAssetTechnicalResponse
    >;
}

export const useEditOnPremVideoAssetTechnical = ({
    invalidationKey
}: {
    invalidationKey?: string;
}): UseEditOnPremAsset => {
    const assetsApi = useAssetsApi();
    const sprintQueryClient = useSprintQueryClient();

    const validateOnPremVideoAssetTechnical = useSprintMutation<
        ValidateOnPremVideoAssetTechnicalParams,
        ValidationResult
    >({
        mutationFn: async ({ assetId, aspectRatioId }): Promise<ValidationResult> => {
            if (!assetId) {
                throw new Error("Asset ID is required to validate video asset technical details.");
            }

            return assetsApi.validateOnPremVideoAssetUniqueness({ assetId, aspectRatioId });
        }
    });

    const updateOnPremVideoAssetTechnical = useSprintMutation<
        UpdateOnPremVideoAssetTechnicalRequest,
        UpdateOnPremVideoAssetTechnicalResponse
    >({
        mutationFn: async ({
            assetId,
            aspectRatioId,
            aspectTypeId,
            bitrate,
            duration,
            encoding,
            framerate,
            resolution,
            standardId
        }: UpdateOnPremVideoAssetTechnicalRequest) => {
            if (!assetId) {
                throw new Error("Asset ID is required to update video asset technical details.");
            }

            return assetsApi.updateOnPremVideoAssetTechnical({
                assetId,
                aspectRatioId,
                aspectTypeId,
                standardId,
                bitrate,
                duration,
                encoding,
                framerate,
                resolution
            });
        },
        onSettled: async () => {
            await sprintQueryClient.invalidateQueries({
                queryKey: [OnPremAssetQueryKey.OnPremAssetDetails, invalidationKey]
            });
        }
    });

    return {
        validateOnPremVideoAssetTechnical,
        updateOnPremVideoAssetTechnical
    };
};
